import { useMemo } from 'react'

import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  isEqual,
  startOfMonth,
  subDays,
} from 'date-fns/esm'
import { useSelector } from 'react-redux'

import { selectTerritory } from '@/features/domain/territory'

export function useOperationsCalendarData(firstDayNotArchived: Date) {
  const {
    licensingLimits: { planningRange },
  } = useSelector(selectTerritory)

  const calendarData = useMemo(() => {
    const minDate = startOfMonth(firstDayNotArchived)
    const lastSelectableDate = addDays(firstDayNotArchived, planningRange - 1)
    const maxDate = endOfMonth(lastSelectableDate)

    /**
     * Let's disable all dates between the minimum date on the calendar and the first NOT archived day
     *
     * NOTE: If firstDayNotArchived is equal to the start of his month (minDate) we don't have to
     *       disable anything before it
     */
    const disabledDates = !isEqual(minDate, firstDayNotArchived)
      ? eachDayOfInterval({
          start: minDate,
          end: subDays(firstDayNotArchived, 1),
        })
      : []

    const disabledDays: Record<string, number> = disabledDates.reduce((acc, d) => {
      acc[format(d, 'yyyyMMdd')] = 1
      return acc
    }, {})

    return {
      minDate,
      maxDate,
      disabledDays,
      lastSelectableDate,
      minDateAsString: format(minDate, 'yyyyMMdd'),
      maxDateAsString: format(maxDate, 'yyyyMMdd'),
      firstNonArchivedDay: format(firstDayNotArchived, 'yyyyMMdd'),
    }
  }, [planningRange, firstDayNotArchived])

  return {
    calendarData,
  }
}
